import { BoletraError } from '../errors.js';

/**
 * Mínimo por moneda para ticket types **pagos**. Los tickets gratis
 * (priceCents === 0) siempre están permitidos. El piso es ≈ 1 USD equivalente,
 * elegido para estar siempre por encima del mínimo de Stripe por moneda
 * (Stripe rechaza el PaymentIntent con `amount_too_small` si el monto cae
 * abajo de su umbral por moneda — ej. MX$10 para MXN, ¥50 para JPY).
 *
 * Las cantidades acá son en *Stripe minor units*:
 *   - Para monedas decimales (USD, EUR, MXN, BRL, ...): centavos. `100` = 1.00.
 *   - Para monedas zero-decimal según Stripe (JPY, CLP, KRW, ...): unidades
 *     enteras. `150` = ¥150 (no ¥1.50).
 *
 * Si una moneda no está en este mapa, asumimos $1 USD = 100 minor units por
 * default (la moneda igual está permitida pero usa el piso de USD). Esto evita
 * romper hard al agregar un país nuevo a `SUPPORTED_COUNTRIES` sin actualizar
 * este archivo primero, pero el typecheck en CI debería atraparlo (ver test).
 *
 * Cuando movimientos de FX hagan que un piso quede demasiado lejos de $1 USD
 * (>20% drift), actualizar este archivo y los tests asociados.
 */
export const MIN_PAID_TICKET_AMOUNT: Record<string, number> = {
  USD: 100,
  CAD: 140,
  EUR: 100,
  GBP: 80,
  MXN: 2000,
  ARS: 100_000,
  BRL: 510,
  CLP: 1000,
  COP: 400_000,
  PEN: 380,
  UYU: 4000,
  AUD: 160,
  NZD: 170,
  JPY: 150,
  SGD: 140,
};

const DEFAULT_MIN_MINOR_UNITS = 100;

/**
 * Returns the minimum allowed amount (in minor units) for a paid ticket in
 * the given currency. Free tickets (amount = 0) are always allowed and don't
 * need to clear this floor.
 */
export function getMinPaidTicketAmount(currency: string): number {
  const upper = currency.toUpperCase();
  return MIN_PAID_TICKET_AMOUNT[upper] ?? DEFAULT_MIN_MINOR_UNITS;
}

/**
 * Soft-validation: returns whether a price is allowed without throwing.
 * Used by the frontend to enable/disable form submit.
 */
export function isValidTicketPrice(amountCents: number, currency: string): boolean {
  if (!Number.isFinite(amountCents) || amountCents < 0 || !Number.isInteger(amountCents)) {
    return false;
  }
  if (amountCents === 0) return true;
  return amountCents >= getMinPaidTicketAmount(currency);
}

/**
 * Hard-validation: throws BoletraError('validation', ...) when the price is
 * below the per-currency minimum. Used by the API to block ticket
 * creation/update and to gate checkout.
 *
 * Free tickets (amountCents === 0) always pass. Negative or non-integer
 * amounts fail with a separate message.
 */
export function assertTicketPriceValid(amountCents: number, currency: string): void {
  if (!Number.isFinite(amountCents) || !Number.isInteger(amountCents) || amountCents < 0) {
    throw new BoletraError('validation', 'Ticket price must be a non-negative integer', 422, {
      field: 'priceCents',
      currency: currency.toUpperCase(),
      received: amountCents,
    });
  }
  if (amountCents === 0) return;
  const min = getMinPaidTicketAmount(currency);
  if (amountCents < min) {
    throw new BoletraError(
      'validation',
      `Ticket price must be at least ${formatMinForMessage(min, currency)} (≈ 1 USD). Use 0 for free tickets.`,
      422,
      {
        field: 'priceCents',
        currency: currency.toUpperCase(),
        minAmount: min,
        received: amountCents,
      },
    );
  }
}

/**
 * Stripe zero-decimal currencies in our supported set. These store
 * the amount as major units, not minor (e.g. JPY 150 = ¥150, not ¥1.50).
 * Used only for formatting the validation error message — the FE handles
 * proper Intl formatting on its own.
 */
const ZERO_DECIMAL_CURRENCIES = new Set(['JPY', 'CLP', 'KRW', 'VND', 'XOF', 'XAF']);

function formatMinForMessage(minMinorUnits: number, currency: string): string {
  const upper = currency.toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(upper)) {
    return `${upper} ${minMinorUnits.toLocaleString('en-US')}`;
  }
  const major = (minMinorUnits / 100).toFixed(2);
  return `${upper} ${major}`;
}
